"use client";

import { useState } from "react";
import { Star } from "lucide-react";

import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import { RatingBlock as RatingBlockType } from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import { useBlockEditable } from "../../../hooks/useBlockEditable";
import RichText from "../text/richText";
import { PreviewButton } from "../shared/PreviewButton";
import RichHtml from "./richHtml";
import { seedTitleHtml, stripHtml } from "../../../lib/titleHtml";
import { fieldTag } from "../../../lib/sectionRegistry";
import { useLomdaLocale } from "../../../hooks/useLomdaLocale";

/** Star row copied from ../video-player (number inside each star, yellow fill). */
function Stars({
    max,
    rating,
    onPick,
    interactive,
}: {
    max: number;
    rating: number;
    onPick?: (n: number) => void;
    interactive?: boolean;
}) {
    const [hovered, setHovered] = useState(0);
    return (
        <div dir="ltr" className="flex w-full max-w-[420px] justify-between">
            {Array.from({ length: max }, (_, i) => i + 1).map((star) => (
                <div
                    key={star}
                    className={cn("relative", interactive && "cursor-pointer")}
                    onClick={() => interactive && onPick?.(star)}
                    onMouseEnter={() => interactive && setHovered(star)}
                    onMouseLeave={() => interactive && setHovered(0)}
                >
                    <Star
                        className={cn(
                            "size-12 transition-colors sm:size-16",
                            star <= rating
                                ? "fill-yellow-500 text-yellow-500"
                                : "text-[#D5D8EF]",
                            star <= hovered && "text-yellow-500"
                        )}
                        style={{ strokeWidth: 0.5 }}
                    />
                    <span
                        className={cn(
                            "absolute inset-0 flex items-center justify-center text-sm text-[#D5D8EF] sm:text-base",
                            star <= rating && "text-yellow-800"
                        )}
                    >
                        {star}
                    </span>
                </div>
            ))}
        </div>
    );
}

export default function RatingBlock({ block }: { block: RatingBlockType }) {
    const locale = useLomdaLocale();
    const questionTag = fieldTag("rating", "question");
    const editable = useBlockEditable();
    const replaceBlockData = useLomdaV3Editor((s) => s.replaceBlockData);
    const { question, max } = block.data;

    const [rating, setRating] = useState(0);
    const [sent, setSent] = useState(false);

    if (editable) {
        return (
            <div className="lomda-v3-card-surface flex flex-col items-center gap-6 rounded-[20px] border border-[hsl(var(--lomda-border))] p-8">
                <div className="w-full max-w-[560px] text-center">
                    <RichText
                        initialHtml={seedTitleHtml(
                            question,
                            questionTag,
                            "center"
                        )}
                        defaultTag={questionTag}
                        editable
                        useGlobalToolbar
                        blockId={block.id}
                        centerPlaceholder
                        placeholder={t(
                            "lomda-v3-editor.rating.question-placeholder"
                        )}
                        onCommit={(html) =>
                            replaceBlockData(block.id, {
                                ...block.data,
                                question: html,
                            })
                        }
                    />
                </div>
                <Stars max={max} rating={0} />
            </div>
        );
    }

    if (!stripHtml(question)) return null;

    return (
        <div className="lomda-v3-card-surface flex flex-col items-center gap-6 rounded-[20px] border border-[hsl(var(--lomda-border))] p-8">
            <RichHtml html={seedTitleHtml(question, questionTag, "center")} />
            <Stars
                max={max}
                rating={rating}
                interactive={!sent}
                onPick={setRating}
            />
            {sent ? (
                <span className="lomda-v3-text-p text-[hsl(var(--lomda-primary))] animate-in fade-in">
                    {t("lomda-v3-editor.rating.thanks", locale)}
                </span>
            ) : (
                <PreviewButton
                    type="button"
                    disabled={rating <= 0}
                    onClick={() => setSent(true)}
                >
                    {t("lomda-v3-editor.rating.send", locale)}
                </PreviewButton>
            )}
        </div>
    );
}
